// Attendance detail drawer — opened by a records-table row click. ONE fetch:
// the same attendance-list endpoint narrowed to the person ({ page, limit,
// users: [userId] } — deliberately NO dateFilter: full history), no detail
// endpoint (a record carries everything). Two tabs: Details renders the
// SELECTED record (defaulting to the first of page 1) — punch times ("On
// shift" while open), live shift total, punch photos, the clock-out note and
// the Where map (clock-out point once ended, clock-in before); Timesheet is
// the person's paginated shift table whose row click changes the selection
// (the selected record is held whole, so paging can't orphan it). The mock's
// structure sub-line and address line are omitted — no payload fields.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleMap, OverlayViewF, OverlayView, useJsApiLoader } from '@react-google-maps/api';
import {
  Avatar,
  Chip,
  DataTable,
  Drawer,
  Icon,
  TableFooter,
  Tabs,
  type ChipVariant,
} from '@/components/common';
import useStickyTotal from '@/hooks/useStickyTotal';
import {
  ClockInState,
  useGetAttendanceListQuery,
  type AttendanceRecord,
} from '@/api/endpoints/attendanceApi';
import { useDownloadClockInMediaQuery } from '@/api/endpoints/mediaApi';
import { MAP_API_KEY } from '@/config/config';
import toneOf from '@/utils/avatarTone';
import i18n from '@/i18n/config';
import { fmtShiftDuration, useTimesheetColumns } from './attendanceColumns';
import { fmtClock } from './utils/lastSeen';
import initialsOf from '@/utils/initials';

/** Selected record's state → header chip + Details copy (the pin-card
 *  vocabulary: an open shift reads "Checked in", not the table's "Working
 *  now"). */
const DRAWER_STATE_META: Record<ClockInState, { chip: ChipVariant; labelKey: string }> = {
  [ClockInState.Started]: { chip: 'success', labelKey: 'attendance_pin_checked_in' },
  [ClockInState.Paused]: { chip: 'warning', labelKey: 'attendance_status_paused' },
  [ClockInState.Ended]: { chip: 'neutral', labelKey: 'attendance_status_out' },
};

const MAP_ZOOM = 15;

/** The Where point: the clock-out position once the shift ended (when the
 *  wire carries it), the clock-in position before. */
const whereOf = (r: AttendanceRecord) =>
  r.state === ClockInState.Ended && r.location.clockOutLat !== undefined
    ? {
        lat: r.location.clockOutLat,
        lng: r.location.clockOutLng as number,
        accuracy: r.accuracy.clockOutAccuracy,
      }
    : {
        lat: r.location.clockInLat,
        lng: r.location.clockInLng,
        accuracy: r.accuracy.clockInAccuracy,
      };

/** One punch photo slot: the resolved photo, or the dashed "None" box. */
const PhotoSlot = ({ url, none }: { url?: string; none: string }) =>
  url ? (
    <img className='attendance-drawer__photo' src={url} alt='' />
  ) : (
    <span className='attendance-drawer__photo attendance-drawer__photo--none'>{none}</span>
  );

/** The Where card's map — one avatar pin on the punch point. Shares the
 *  live-locations loader id, so the Maps script is only ever loaded once. */
const WhereMap = ({ record }: { record: AttendanceRecord }) => {
  const { t } = useTranslation();
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: MAP_API_KEY,
    language: i18n.language,
  });
  const { lat, lng } = whereOf(record);

  if (!MAP_API_KEY || loadError) {
    return (
      <div className='attendance-drawer__map attendance-drawer__map--placeholder'>
        <Icon name='map-pin' size={24} aria-hidden='true' />
        {loadError ? t('attendance_live_map_error') : null}
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div
        className='attendance-drawer__map attendance-drawer__map--placeholder'
        aria-hidden='true'
      />
    );
  }
  return (
    <div className='attendance-drawer__map'>
      <GoogleMap
        mapContainerClassName='attendance-drawer__map-canvas'
        center={{ lat, lng }}
        zoom={MAP_ZOOM}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: true,
          clickableIcons: false,
          cameraControl: false,
        }}
      >
        <OverlayViewF
          position={{ lat, lng }}
          mapPaneName={OverlayView.OVERLAY_LAYER}
          getPixelPositionOffset={() => ({ x: 0, y: 0 })}
        >
          <div className='attendance-live__pin-wrap'>
            <span className='attendance-live__pin'>
              <Avatar
                initials={initialsOf(record.fullName)}
                size={30}
                tone={toneOf(record.fullName)}
              />
            </span>
          </div>
        </OverlayViewF>
      </GoogleMap>
    </div>
  );
};

interface AttendanceDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  /** From the clicked row. */
  userId: string;
  fullName: string;
  /** The clicked row's record — pre-selected when it's in the fetched items
   *  (falls back to the first record otherwise). */
  initialRecordId?: string;
}

const AttendanceDetailDrawer = ({
  open,
  onClose,
  userId,
  fullName,
  initialRecordId,
}: AttendanceDetailDrawerProps) => {
  const { t } = useTranslation();

  const [tab, setTab] = useState<'details' | 'timesheet'>('details');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selected, setSelected] = useState<AttendanceRecord | null>(null);

  // A fresh open (or another row) starts over.
  useEffect(() => {
    if (open) {
      setTab('details');
      setPage(1);
      setSelected(null);
    }
  }, [open, userId, initialRecordId]);

  const { data, currentData, isLoading, isFetching, isError } = useGetAttendanceListQuery(
    { users: [userId], page, limit },
    { skip: !open, refetchOnMountOrArgChange: true },
  );
  const items = useMemo(() => data?.items ?? [], [data]);
  const total = useStickyTotal(currentData?.total, page);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  // Default selection: the clicked row's record when this page has it,
  // otherwise the first (most recent) record.
  useEffect(() => {
    if (open && !selected && items.length > 0)
      setSelected(items.find((r) => r.id === initialRecordId) ?? items[0]);
  }, [open, selected, items, initialRecordId]);

  // Timesheet thumbs: one batch per drawer page.
  const pageRefs = useMemo(
    () =>
      items.flatMap((r) =>
        [r.clockInPhoto, r.clockOutPhoto].filter((p): p is NonNullable<typeof p> => Boolean(p)),
      ),
    [items],
  );
  const { data: pageUrls } = useDownloadClockInMediaQuery(pageRefs, {
    skip: !open || pageRefs.length === 0,
  });
  // Details thumbs resolve on their own: the selected record may belong to a
  // page whose batch cache has since been evicted.
  const selectedRefs = useMemo(
    () =>
      selected
        ? [selected.clockInPhoto, selected.clockOutPhoto].filter((p): p is NonNullable<typeof p> =>
            Boolean(p),
          )
        : [],
    [selected],
  );
  const { data: selectedUrls } = useDownloadClockInMediaQuery(selectedRefs, {
    skip: !open || selectedRefs.length === 0,
  });

  const columns = useTimesheetColumns(pageUrls ?? {});

  const meta = selected ? DRAWER_STATE_META[selected.state] : null;
  const dayLabel = selected
    ? new Intl.DateTimeFormat(i18n.language, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(new Date(selected.clockInDate))
    : '';
  const weekdayLabel = selected
    ? new Intl.DateTimeFormat(i18n.language, { weekday: 'long' }).format(
        new Date(selected.clockInDate),
      )
    : '';
  const where = selected ? whereOf(selected) : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      closeOnOverlay
      className='attendance-drawer'
      header={
        <div className='attendance-drawer__header'>
          <Avatar initials={initialsOf(fullName)} size={44} tone={toneOf(fullName)} />
          <span className='attendance-drawer__header-name'>{fullName}</span>
          {meta && (
            <span className='attendance-drawer__header-state'>
              <Chip variant={meta.chip} dot>
                {t(meta.labelKey)}
              </Chip>
            </span>
          )}
        </div>
      }
    >
      {/* Edge-to-edge tab strip under the header — the AddPeopleDrawer chrome
          idiom (header border off, body unpadded on --bg, tabs on surface). */}
      <Tabs
        value={tab}
        onChange={(v) => setTab(v as 'details' | 'timesheet')}
        aria-label={t('attendance_title')}
        items={[
          { value: 'details', label: t('attendance_drawer_tab_details'), icon: 'id-card' },
          { value: 'timesheet', label: t('attendance_drawer_tab_timesheet'), icon: 'table' },
        ]}
      />

      <div className='attendance-drawer__body'>
        {tab === 'details' &&
          (selected ? (
            <>
              <section className='attendance-drawer__card'>
                <header className='attendance-drawer__day'>
                  <span className='attendance-drawer__day-date'>
                    <Icon name='calendar-days' size={18} aria-hidden='true' />
                    {dayLabel}
                  </span>
                  <span className='attendance-drawer__day-name'>{weekdayLabel}</span>
                </header>
                <div className='attendance-drawer__facts'>
                  <div className='attendance-drawer__fact'>
                    <span className='attendance-drawer__fact-label'>
                      {t('attendance_col_check_in')}
                    </span>
                    <span className='attendance-drawer__fact-value'>
                      <Icon name='log-in' size={16} aria-hidden='true' />
                      {fmtClock(selected.clockInDate)}
                    </span>
                  </div>
                  <div className='attendance-drawer__fact'>
                    <span className='attendance-drawer__fact-label'>
                      {t('attendance_col_check_out')}
                    </span>
                    <span className='attendance-drawer__fact-value'>
                      <Icon name='log-out' size={16} aria-hidden='true' />
                      {selected.clockOutDate
                        ? fmtClock(selected.clockOutDate)
                        : t('attendance_on_shift')}
                    </span>
                  </div>
                  <div className='attendance-drawer__fact'>
                    <span className='attendance-drawer__fact-label'>
                      {t('attendance_col_total_hours')}
                    </span>
                    <span className='attendance-drawer__fact-value'>
                      <Icon name='clock' size={16} aria-hidden='true' />
                      {fmtShiftDuration(selected, t)}
                    </span>
                  </div>
                </div>
                <div className='attendance-drawer__photos'>
                  <div className='attendance-drawer__photo-slot'>
                    <span className='attendance-drawer__fact-label'>
                      {t('attendance_photo_in')}
                    </span>
                    <PhotoSlot
                      url={
                        selected.clockInPhoto
                          ? selectedUrls?.[selected.clockInPhoto.imageName]
                          : undefined
                      }
                      none={t('attendance_photo_none')}
                    />
                  </div>
                  <div className='attendance-drawer__photo-slot'>
                    <span className='attendance-drawer__fact-label'>
                      {t('attendance_photo_out')}
                    </span>
                    <PhotoSlot
                      url={
                        selected.clockOutPhoto
                          ? selectedUrls?.[selected.clockOutPhoto.imageName]
                          : undefined
                      }
                      none={t('attendance_photo_none')}
                    />
                  </div>
                </div>
                <div className='attendance-drawer__notes'>
                  <div className='attendance-drawer__note-slot'>
                    <span className='attendance-drawer__fact-label'>{t('attendance_note_in')}</span>
                    <p
                      className={[
                        'attendance-drawer__note-text',
                        !selected.clockInNotes && 'attendance-drawer__note-text--empty',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {selected.clockInNotes ?? '—'}
                    </p>
                  </div>
                  <div className='attendance-drawer__note-slot'>
                    <span className='attendance-drawer__fact-label'>
                      {t('attendance_note_out')}
                    </span>
                    <p
                      className={[
                        'attendance-drawer__note-text',
                        !selected.clockOutNotes && 'attendance-drawer__note-text--empty',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {selected.clockOutNotes ?? '—'}
                    </p>
                  </div>
                </div>
              </section>

              <section className='attendance-drawer__card'>
                <header className='attendance-drawer__where-head'>
                  <span className='attendance-drawer__where-title'>
                    <Icon name='map-pin' size={18} aria-hidden='true' />
                    {t('attendance_where_title')}
                  </span>
                  {where && (
                    <a
                      className='attendance-drawer__maps-link'
                      href={`https://www.google.com/maps?q=${where.lat},${where.lng}`}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('attendance_open_in_maps')}
                      <Icon name='external-link' size={14} aria-hidden='true' />
                    </a>
                  )}
                </header>
                <WhereMap record={selected} />
                {where && (
                  <footer className='attendance-drawer__where-foot'>
                    <span className='attendance-drawer__coords'>
                      {`${where.lat.toFixed(5)}, ${where.lng.toFixed(5)}`}
                    </span>
                    <span className='attendance-drawer__accuracy'>
                      {t('attendance_accuracy', { m: Math.round(Number(where.accuracy)) })}
                    </span>
                  </footer>
                )}
              </section>
            </>
          ) : (
            <div className='attendance-drawer__empty'>
              <Icon name='calendar-check' size={26} aria-hidden='true' />
              {isLoading ? t('text_loading') : t('attendance_empty')}
            </div>
          ))}

        {tab === 'timesheet' && (
          <section className='attendance-drawer__card attendance-drawer__card--table'>
            <header className='attendance-drawer__where-head'>
              <span className='attendance-drawer__where-title'>
                <Icon name='table' size={18} aria-hidden='true' />
                {t('attendance_timesheet_title')}
              </span>
              <span className='attendance-drawer__hint'>{t('attendance_timesheet_hint')}</span>
            </header>
            <DataTable
              columns={columns}
              rows={items}
              rowKey={(r) => r.id}
              loading={isLoading || isFetching}
              error={isError}
              loadingText={t('text_loading')}
              errorText={t('error_generic')}
              minWidth={640}
              onRowClick={(r) => setSelected(r)}
              isRowActive={(r) => r.id === selected?.id}
              empty={
                <>
                  <Icon name='calendar-check' size={26} />
                  {t('attendance_empty')}
                </>
              }
            />
            <TableFooter
              page={page}
              limit={limit}
              total={total}
              limits={[10, 20, 50]}
              onPage={setPage}
              onLimit={(n) => {
                setLimit(n);
                setPage(1);
              }}
              perPageLabel={t('attendance_items_per_page')}
              rangeText={t('attendance_range', { from, to, total })}
            />
          </section>
        )}
      </div>
    </Drawer>
  );
};

export default AttendanceDetailDrawer;
